import { FC } from 'react';

import AnimatronAddress from './AnimatronAddress';
import TermsUseStyle, { LastUpdated } from './TermsUseStyle';

export type TermsUseSectionProps = {
  supportEmail: string;
  contactEmail: string;
  webSite: string;
  product: string;
};

const TermsUseSection: FC<TermsUseSectionProps> = ({
  supportEmail,
  contactEmail,
  webSite,
  product
}) => (
  <>
    <TermsUseStyle>
      <h1>END USER TERMS OF USE AGREEMENT</h1>

      <p>
        PLEASE READ CAREFULLY THE TERMS AND CONDITIONS OF THIS ANIMATRON INC.
        END USER TERMS OF USE AGREEMENT (“AGREEMENT”). THIS IS A LEGAL AGREEMENT
        BETWEEN ANIMATRON (“ANIMATRON”, “we” and/or “us”) AND YOU ("YOU"). THIS
        AGREEMENT GOVERNS YOUR ACCESS TO AND USE OF ALL SERVICES AND PRODUCTS
        PROVIDED BY OR THROUGH THE WEBSITE{' '}
        <b>
          <i>
            <a href={`https://${webSite}`}>{webSite}</a>
          </i>
        </b>{' '}
        (the “SERVICE”).
      </p>
      <p>
        By accessing, signing up, and using the Service you agree and
        acknowledge your understanding and acceptance to be bound by all the
        terms and conditions in this Agreement.
      </p>
      <p>
        If you do not agree to be bound by the terms and conditions of this
        Agreement, you may not sign up and use the Service.
      </p>
      <p>
        If you are entering into this Agreement on behalf of a company or other
        organization, your access and use of the Service means that you
        represent and warrant that you have the authority to bind the company or
        organization to this Agreement and commit funds on its behalf. In this
        case the terms "you" and "your" herein will refer to that company or
        organization.
      </p>

      <h2>1. Eligibility and Accounts</h2>

      <p>
        You must be at least thirteen (13) years of age to use the Service. Use
        of the Service is not permitted where prohibited by law.
      </p>
      <p>
        You must create an account with us (an “Account”) to use the Service to
        its fullest extent. You represent and warrant that all information you
        provide to us in creating your Account is complete and accurate. You
        shall update such information when it changes or when we request.
      </p>
      <p>
        You shall not use another person or entity’s Account without
        authorization. You shall be solely responsible for maintaining the
        confidentiality of and restricted access to your Account. You shall be
        solely responsible for all activities that occur under your Account. You
        shall notify us immediately of any breach of security or unauthorized
        use of your Account. Pursuant to the terms herein, we shall not be
        liable for any losses resulting from any unauthorized use of your
        Account. You acknowledge and understand that anyone who gains access to
        your Account will gain access to your all of your data on your Account,
        including any private content.
      </p>

      <h2>2. Grant of Limited License and Termination</h2>

      <p>
        Conditioned upon your compliance with the terms and conditions of this
        Agreement, Animatron hereby grants to you a limited, non-exclusive,
        non-transferable, non-sublicensable, non-assignable, revocable, license
        to use the Service solely for your use on a "Device" (whether mobile
        device or other, e.g., your mobile phone, PDA, computer) owned or
        controlled by you or the company you represent.
      </p>
      <p>
        Except for the rights explicitly granted in this Agreement, Animatron
        reserves all rights in the Service, including all content available
        therein and on your Device (the “Content”), and including all
        intellectual property rights. Animatron may use third party software
        that is subject to open source and/or third party license terms. You are
        subject to those terms.
      </p>
      <p>
        This grant is terminable by us at will for any reason and at our sole
        discretion, with or without prior notice. Upon termination, we may, but
        shall not be obligated to: (I) delete or deactivate your account, (II)
        block your e-mail and/or IP addresses or otherwise terminate your use of
        and ability to use the Service, and/or (III) remove and/or delete any of
        your User Submissions (defined below). You agree not to use or attempt
        to use the Service after said termination. Upon termination, the grant
        of your right to use the Service shall terminate, but all other portions
        of these Terms shall survive. You acknowledge that we are not
        responsible to you or any third party for the termination of your grant
        of use.
      </p>

      <h2>3. Intellectual Property</h2>

      <p>
        The Content on the Service, excluding User Submissions and Third Party
        Content (defined below), but including other text, graphical images,
        photographs, music, video, software, scripts and trademarks, service
        marks and logos contained therein (collectively “Proprietary
        Materials”), are owned by and/or licensed to us. All Proprietary
        Materials are subject to copyright, trademark and/or other rights under
        the laws of applicable jurisdictions, including domestic laws, foreign
        laws, and international conventions. The Service, including all
        intellectual property rights therein and thereto, are the property of
        Animatron or its subsidiaries or affiliated companies and/or third party
        licensors. Animatron reserves all rights not expressly granted in this
        Agreement. You shall not acquire any right, title or interest to the
        Proprietary Materials, whether by implication, estoppel, or otherwise,
        except for the limited rights set forth in this Agreement. Except as
        otherwise explicitly permitted, you agree not to copy, modify, publish,
        transmit, distribute, participate in the transfer or sale of, create
        derivative works of, or in any other way exploit, in whole or in part,
        any Content or Proprietary Materials.
      </p>

      <p>
        You may be held legally responsible for any copyright infringement that
        is caused or encouraged by your failure to abide by the terms of this
        Agreement.
      </p>

      <h2>4. Restrictions on User Submissions</h2>

      <p>
        You are entirely responsible for any and all materials you create,
        upload, submit or otherwise make available via the Service, including
        videos, animations or any other communications or profile information
        (collectively, “User Submissions”). User Submissions cannot always be
        withdrawn. You acknowledge that any disclosure of personal information
        in User Submissions may make you personally identifiable and that we do
        not guarantee any confidentiality with respect to User Submissions.
      </p>
      <p>
        <i>
          All use by you of the Service and the Content to create User
          Submissions is specifically subject to the limitation that the Service
          and Content must be incorporated into any User Submissions with
          substantial value added by you such that the transformed or derivative
          User Submissions are not recognizable as Animatron Service or Content,
          nor shall the Service or Content be capable of being downloaded,
          extracted or accessed by a third-party as a stand-alone file.
          Satisfaction of these conditions shall constitute the User Submissions
          as Derivative Work. Accordingly, you must ensure that the Service and
          Content is only used to create Derivative Work.
        </i>
      </p>
      <p>
        You shall be solely responsible for any and all of your own User
        Submissions and any and all consequences of posting, uploading,
        publishing or otherwise making them available. For any of your User
        Submissions, you affirm, represent and/or warrant that:
      </p>
      <ol>
        <li>
          You own or have the necessary licenses, permissions, rights or
          consents to use and authorize us to use all trademarks, copyrights,
          trade secrets or other proprietary rights in and to User Submissions
          for any and all uses contemplated by the Service and these Terms; and
        </li>
        <li>
          You have written consent, release, and/or permission from each and
          every identifiable individual in the User Submission to use the name
          and/or likeness of each and every such identifiable individual to
          enable use of the User Submission for any and all uses contemplated by
          the Service and these Terms
        </li>
      </ol>
      <p>You further agree that you shall not submit material that:</p>
      <ol start={3}>
        <li>
          Is copyrighted, protected by trade secret or trademark laws, or
          otherwise subject to third party proprietary rights, including privacy
          and publicity rights, unless you are the owner of such rights or have
          explicit permission from the rightful owner to submit the material and
          to grant us all of the license rights granted herein;
        </li>
        <li>
          Is obscene, vulgar, illegal, unlawful, defamatory, fraudulent,
          libelous, harmful, harassing, abusive, threatening, invasive of
          privacy or publicity rights, hateful, racially or ethnically
          offensive, inflammatory, or otherwise inappropriate as decided by us
          in our sole discretion;
        </li>
        <li>
          Depicts illegal activities, promotes or depicts physical harm or
          injury against any group or individual, or promotes or depicts any act
          of cruelty to animals;
        </li>
        <li>
          Impersonates any person or entity or otherwise misrepresents you in
          any way, including creating a false identity;
        </li>
        <li>
          Would constitute, encourage or provide instructions for a criminal
          offense, a violation of the rights of any party, or that would
          otherwise create liability or violate any local, state, national or
          international law;
        </li>
        <li>
          Is unsolicited or unauthorized advertising, promotion, “spam” or any
          other form of solicitation;
        </li>
        <li>Is sexually explicit or suggestive;</li>
        <li>
          Provides instructions on how to assemble explosive/incendiary devices
          or homemade/improvised firearms;
        </li>
        <li>Depicts animal cruelty or extreme violence towards animals;</li>
        <li>Makes false or misleading claims about vaccination safety;</li>
        <li>
          Claims that mass tragedies are hoaxes or false flag operations a)
          portrays any person depicted therein in a way that a reasonable person
          would find offensive, including but not limited to depicting any
          person: a) in connection with pornography, "adult videos", adult
          entertainment venues, escort services, dating services, or the like;
          b) in connection with the advertisement or promotion of tobacco
          products; c) as endorsing a political party, candidate, elected
          official, or opinion; d) as suffering from, or medicating for, a
          physical or mental ailment; or e) engaging in immoral or criminal
          activities.
        </li>
      </ol>

      <p>
        We claim no ownership or control over User Submissions or Third Party
        Content. You or a third party licensor, as appropriate, retain all
        copyrights to User Submissions and you are responsible for protecting
        those rights as appropriate. You irrevocably grant us a world-wide,
        non-exclusive, royalty-free, perpetual, non-cancelable, sublicenseable
        license to reproduce, publicly perform, publicly display, distribute,
        adapt, modify, publish, translate, create derivative works of and
        otherwise exploit User Submissions for any purpose, including without
        limitation any purpose contemplated by the Service and these Terms.
        Furthermore, you also grant other users of the Service a right and
        license to display, stream and download User Submissions in connection
        with their use of the Service and for other personal use.
      </p>
      <p>
        You represent and warrant that you have all the rights, power and
        authority necessary to grant the rights granted herein to User
        Submissions. Specifically, you represent and warrant that you own the
        title to the User Submissions, that you have the right to upload the
        User Submissions to the Service, and that uploading the User Submissions
        will not infringe upon any other party’s rights or your contractual
        obligations to other parties.
      </p>
      <p>
        You acknowledge that we may at our sole discretion refuse to publish,
        remove, or block access to any User Submission for any reason, or for no
        reason at all, with or without notice.
      </p>
      <p>
        You acknowledge that if a User Submission is marked as private, we will
        undertake commercially reasonable means to keep such content from
        becoming publicly available on the Service, but that our obligations and
        liability are limited by the further terms of this agreement.
      </p>
      <p>
        Without limiting the other indemnification provisions herein, you agree
        to defend us against any claim, demand, suit or proceeding made or
        brought against us by a third-party alleging that your User Submissions
        or your use of the Service in violation of these Terms infringes or
        misappropriates the intellectual property rights of any third-party or
        violates applicable law and you shall indemnify us for any and all
        damages against us and for reasonable attorney’s fees and other costs
        incurred by us in connection with any such claim, demand, suit or
        proceeding.
      </p>

      <h2>5. Content on the Service</h2>

      <p>
        You understand and acknowledge that, when using the Service, you will be
        exposed to content from a variety of sources including content made
        available on the Service by other users and through automated or other
        means (collectively, “Third Party Content”) and that we do not control
        and are not responsible for any Third Party Content. You understand and
        acknowledge that you may be exposed to content that is inaccurate,
        offensive, indecent or otherwise objectionable and, without limiting the
        other limitation of liability provisions herein, you agree to waive, and
        hereby do waive, any legal or equitable rights or remedies you may have
        against us with respect thereto.
      </p>
      <p>
        We claim no ownership or control over Third Party Content. Third parties
        retain all rights to Third Party Content and they are responsible for
        protecting their rights as appropriate.
      </p>
      <p>
        You understand and acknowledge that we assume no responsibility
        whatsoever for monitoring the Service for inappropriate content or
        conduct. If at any time we choose, in our sole discretion, to monitor
        such content, we assume no responsibility for such content, have no
        obligation to modify or remove any such content (including User
        Submissions and Third Party Content), and assume no responsibility for
        the conduct of others submitting any such content (including User
        Submissions and Third Party Content).
      </p>
      <p>
        Without limiting the provisions below on limitations of liability and
        disclaimers of warranties, all Content (including User Submissions and
        Third Party Content) on the Service is provided to you “AS-IS” for your
        information and personal use only and you shall not use, copy,
        reproduce, distribute, transmit, broadcast, display, sell, license or
        otherwise exploit for any other purpose whatsoever the Content without
        the prior written consent of the respective owners/licensors of the
        Content.
      </p>

      <h2>6. User Conduct</h2>
      <p>
        You represent and warrant that all the information and content provided
        by you to us is accurate and current and that you have all necessary
        rights, power and authority to (I) agree to these Terms, (II) provide
        the User Submissions to us, and (III) perform the acts required of you
        under these Terms.
      </p>
      <p>As a condition of your use of the Service:</p>

      <ol>
        <li>
          You agree not to use the Service for any unlawful purpose or in any
          way that is prohibited by these Terms;
        </li>
        <li>
          You agree to abide by all applicable local, state, national and
          international laws and regulations;
        </li>
        <li>
          You agree not to use the Service in any way that exposes us to
          criminal or civil liability;
        </li>
        <li>
          You agree that you are solely responsible for all acts and omissions
          that occur as a result of your use of the Service;
        </li>
        <li>
          You agree that all your User Submissions that you provide to us
          belongs to you and that you have the right and authority to provide it
          to us;
        </li>
        <li>
          You agree to maintain the security of your login password and to be
          fully responsible for any and all use of your account;
        </li>
        <li>
          You agree not to use or attempt to use any other party’s account on
          the Service without authorization;
        </li>
        <li>
          You agree not to use any automated means, including robots, crawlers
          or data mining tools, to download, monitor or use data or Content from
          the Service;
        </li>
        <li>
          You agree not to use the Service to collect usernames and/or e-mail
          addresses for sending unsolicited messages of any kind;
        </li>
        <li>
          You agree not to take any action that imposes, or may impose, in our
          sole discretion, an unreasonable or disproportionately large load on
          our technology infrastructure or otherwise make excessive demands on
          it;
        </li>
        <li>
          You agree not to “stalk” or otherwise harass anyone on or through the
          Service;
        </li>
        <li>
          You agree not to forge headers or otherwise manipulate identifiers in
          order to disguise the origin of any information you transmit;
        </li>
        <li>
          You agree not to disable, circumvent, or otherwise interfere with
          security related features of the Service or features that prevent or
          restrict use or copying of any content or which enforce limitations on
          the use of the Service or the content therein;
        </li>
        <li>
          You agree not to post, link to, or otherwise make available on the
          Service any material that contains software viruses or any computer
          code, file or program designed to interrupt, destroy, limit or monitor
          the functionality of any computer software or hardware or any
          telecommunications equipment;
        </li>
        <li>
          You agree not to license, sublicense, sell, resell, transfer, assign,
          distribute or otherwise in any way commercially exploit or make
          available the Service or any Content to any third party;
        </li>
        <li>You agree not to “frame” or “mirror” the Service; and</li>
        <li>You agree not to reverse engineer any portion of the Service.</li>
      </ol>

      <p>
        We reserve the right to take appropriate action against any user for any
        unauthorized use of the Service, including civil, criminal and
        injunctive redress and the termination of any user’s use of the Service.
        Any use of the Service and our computer systems not authorized by these
        Terms is a violation of these Terms and certain international, foreign
        and domestic criminal and civil laws, including the Computer Fraud and
        Abuse Act.
      </p>

      <h2>7. Bandwidth limitations and fees:</h2>

      <p>
        Bandwidth limitations may be presented in the form of views, hours
        viewed or any other form convenient to the Service. All plans are
        subject to monthly bandwidth limits as specified on the {product}
        pricing page. Monthly bandwidth limits are calculated per month and are
        based on the date of account activation (i.e. signup date for free users
        or purchase date for paying users). Once an account reaches its monthly
        bandwidth limit, you will be charged for overages or be asked to upgrade
        to a plan with a higher bandwidth limit. If your plan does not allow for
        overages, your videos will not play for your viewers once your bandwidth
        limitation has been reached.
      </p>

      <h2>8. Fees</h2>

      <p>
        We may charge you a subscription fee to give you full use of the
        Service. Plans, fees and services available will be posted on the
        Service and may change from time to time in our sole discretion. All
        fees are in United States Dollars. Fees are subject to change and may
        vary due to various reasons, including special and limited promotional
        offers. All payments, including subscription fees, must be made with a
        valid credit card or debit card, the information of which may be kept on
        file by us or by our credit card processing contractor (a “Filed Card”).
        Fees for the indicated period of the subscription must be paid in
        advance of the period for which you seek to use it. You may not have
        access to your account and the Service if you have not paid your
        subscription fee for that period.
      </p>
      <p>
        In order to make a purchase, you may be asked to supply certain
        information to allow us to process and authorize your purchase,
        including, without limitation, your name, address, card number, card
        expiration date, shipping information, card security number and other
        information. You represent and warrant that (i) you have the legal right
        to use form of payment that you use and that (ii) the information that
        you are providing with that form of payment is true and correct. You
        acknowledge that we may use a third party for the purposes of processing
        or facilitating any payment and that by submitting your information to
        us you grant us the right to provide this information to such third
        parties.
      </p>
      <p>
        We reserve the right to refuse, cancel or terminate your order for any
        time and for any reason in our sole discretion. Without limiting the
        foregoing, we reserve the right to refuse, cancel or terminate your
        order because of product or service unavailability, errors in the
        description or price of our product or service and errors in your order.
      </p>
      <p>
        For your convenience and satisfaction all subscriptions will
        automatically renew upon expiration unless your subscription is
        cancelled prior to the end of the then current subscription period. Upon
        all automatic renewals, YOUR FILED CARD WILL BE AUTOMATICALLY CHARGED
        FOR THE NEXT SUBSCRIPTION PERIOD. YOU HEREBY AUTHORIZE US AND OUR AGENTS
        (INCLUDING PAYMENT PROCESSORS WE MAY USE) TO CHARGE YOUR FILED CARD FOR
        SUCH PAYMENTS ON YOUR BEHALF.
      </p>
      <p>
        You may cancel your subscription at any time from within your account or
        by contacting customer support. Cancellations must be requested five or
        more business days prior to the end of the billing cycle.{' '}
      </p>
      <p>
        You agree not to report as fraudulent, lost or stolen any form of
        payment which you have used in conjunction with payment to us, for which
        you do not have a good faith reason to believe is in fact fraudulent,
        lost or stolen. You agree not to report as unauthorized any charge by us
        for any goods or services, including subscriptions, for which you do not
        have a good faith reason to believe is in fact unauthorized. You agree
        that, absent good faith, in the event of any such report you shall be
        liable to us for such charge or obligation plus an additional $100
        administrative fee. The liability specified in this paragraph will not
        limit our rights or any other liability you may have for any other
        reason, including a breach of any other provision of these Terms.
      </p>
      <p>
        If at any time we terminate your rights to use the Service because of a
        breach of these Terms, you shall not be entitled to a refund of any
        portion of your fees. In all other respects, such fees shall be governed
        by additional rules, terms, conditions or agreements posted on the
        Service and/or imposed by any sales agent or payment processing company,
        as may be amended from time to time.
      </p>

      <h2>9. Cancellation of Subscription</h2>

      <p>
        You may cancel your subscription at any time from within your account or
        by emailing customer support (
        <a href={`mailto:${supportEmail}`}>{supportEmail}</a>) and requesting an
        account cancellation. Subscription cancellation does not trigger an
        account deletion. Your subscription cancellation date will be at the end
        of your billing cycle. Cancellations that occur within less than 5
        business days of their billing cycle might be canceled at the proceeding
        billing cycle. Any money paid for subscription or money used to purchase
        content and/or system credits are not refundable. Upon cancellation of
        your subscription, all unused credits will be forfeited on the
        subscription cancellation date.
      </p>

      <h2>10. Deletion of account</h2>

      <p>
        We reserve the right to delete inactive or empty free accounts. If an
        account is inactive, we will attempt to notify you of the pending
        account deletion and will proceed to delete it and all its content
        thereafter unless you begin using your account again.
      </p>
      <p>
        Subscription cancellation does not automatically trigger account
        deletion. If you wish to have your account deleted from our servers you
        must email us at <a href={`mailto:${supportEmail}`}>{supportEmail}</a>.
      </p>

      <h2>11. Privacy Policy</h2>
      <p>
        We retain a separate Privacy Policy and your assent to these Terms also
        signifies your assent to the Privacy Policy. We reserve the right to
        amend the Privacy Policy at any time by posting such amendments to the
        Service. No other notification may be made to you about any amendments.
        Your continued use of the Service following such amendments will
        constitute your acceptance of such amendments, regardless of whether you
        have actually read them.
      </p>

      <h2>12. Copyright Claims</h2>
      <p>
        We respect the intellectual property rights of others. You may not
        infringe the copyright, trademark or other proprietary informational
        rights of any party. We may in our sole discretion remove any Content we
        have reason to believe violates any of the intellectual property rights
        of others and may terminate your use of the Service if you submit any
        such Content.
      </p>

      <p>
        <b>
          <i>REPEAT INFRINGER POLICY.</i> AS PART OF OUR REPEAT-INFRINGEMENT
          POLICY, ANY USER FOR WHOSE MATERIAL WE RECEIVE THREE LEGALLY EFFECTIVE
          COMPLAINTS WITHIN ANY CONTIGUOUS SIX-MONTH PERIOD WILL HAVE HIS
          ACCOUNT TERMINATED.
        </b>
      </p>
      <p>
        Pursuant to Title 17, Section 512(c)(2) of the United States Code, if
        you believe that any of your copyrighted material is being infringed on
        the Service, we have designated an agent to receive notifications of
        claimed copyright infringement. Notifications should be e-mailed{' '}
        <a href={`mailto:${contactEmail}`}>{contactEmail}</a> (with the subject
        line “Copyright Infringement”) or sent to:
      </p>

      <p>
        Copyright Agent
        <br />
        6750 N. Andrews Ave., Suite 200
        <br />
        Fort Lauderdale, FL 33309
        <br />
        United States
        <br />
        Fax: (800) 371-0235
        <br />
        Email: <a href={`mailto:${contactEmail}`}>{contactEmail}</a>
      </p>

      <p>
        All notifications not relevant to us or ineffective under the law will
        receive no response or action thereupon. An effective notification of
        claimed infringement must be a written communication to our agent that
        includes substantially the following:
      </p>

      <ol>
        <li>
          Identification of the copyrighted work that is believed to be
          infringed. Please describe the work and, where possible, include a
          copy or the location (e.g., a URL) of an authorized version of the
          work;
        </li>
        <li>
          Identification of the material that is believed to be infringing and
          its location. Please describe the material and provide a URL or any
          other pertinent information that will allow us to locate the material
          on the Service;
        </li>
        <li>
          Information that will allow us to contact you, including your address,
          telephone number and, if available, your e-mail address;
        </li>
        <li>
          A statement that you have a good faith belief that the use of the
          material complained of is not authorized by you, your agent or the
          law;
        </li>
        <li>
          A statement that the information in the notification is accurate and
          that under penalty of perjury that you are the owner or are authorized
          to act on behalf of the owner of the work that is allegedly infringed;
          and
        </li>
        <li>
          A physical or electronic signature from the copyright holder or an
          authorized representative.
        </li>
      </ol>
      <h2>13. Modification of These Terms</h2>

      <p>
        Animatron reserves the right to modify and/or change any of the terms
        and conditions of this Agreement at any time and without prior notice.
        If Animatron materially modifies this Agreement it will make reasonable
        efforts to notify you of the change. You acknowledge that your continued
        use of the website following such amendments will constitute your
        acceptance of such amendments.
      </p>

      <h2>14. Indemnification and Release</h2>

      <p>
        You hereby agree to indemnify us and hold us harmless from any and all
        third-party claims and expenses, including attorney’s fees, arising from
        your use of the Service or from your breach of these Terms.
      </p>
      <p>
        In the event that you have a dispute with one of more other users or any
        third parties, you hereby release us, our officers, employees, agents
        and successors-in-right from claims, demands and damages (actual and
        consequential) of every kind or nature, known and unknown, suspected and
        unsuspected, disclosed and undisclosed, arising out of or in any way
        related to such disputes and/or the Service.
      </p>

      <h2>15. Disclaimer of Warranties and Limitations of Liabilities</h2>

      <p>
        READ THIS SECTION CAREFULLY AS IT LIMITS OUR LIABILITY TO THE MAXIMUM
        EXTENT PERMITTED UNDER APPLICABLE LAW.
      </p>
      <p>
        The Service may contain links to third-party websites or services which
        are independent of us. We assume no responsibility for the content,
        privacy policies, or practices of and make no representation or warranty
        as to the accuracy, completeness or authenticity of information
        contained in any third party website or service. We have no right or
        ability to edit the content of any third party websites or service. You
        acknowledge that we shall not be liable for any and all liability
        arising from your use of any third party website or service.
      </p>
      <p>
        The Service is provided “AS-IS” and without any warranty or condition,
        express, implied or statutory. We specifically disclaim to the fullest
        extent any implied warranties of merchantability, fitness for a
        particular purpose, non-infringement, information accuracy, integration,
        interoperability or quiet enjoyment. We disclaim any warranties for
        viruses or other harmful components in connection with the Service. Some
        jurisdictions do not allow or limit the disclaimer of implied
        warranties, therefore in such jurisdictions, some of the foregoing
        disclaimers may not apply to you or be limited.
      </p>
      <p>
        UNDER NO CIRCUMSTANCES SHALL WE BE LIABLE FOR DIRECT, INDIRECT
        INCIDENTAL, SPECIAL, CONSEQUENTIAL OR EXEMPLARY DAMAGES (EVEN IF WE HAVE
        BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES) RESULTING FROM ANY
        ASPECT OF YOUR USE OF THE SERVICE, WHETHER, WITHOUT LIMITATION, SUCH
        DAMAGES ARISE FROM (I) YOUR USE, MISUSE OR INABILITY TO USE THE SERVICE,
        (II) YOUR RELIANCE ON ANY CONTENT ON THE SERVICE, (III) THE
        INTERRUPTION, SUSPENSION, MODIFICATION, ALTERATION OR COMPLETE
        DISCONTINUANCE OF THE SERVICE OR (IV) THE TERMINATION OF THE SERVICE BY
        US. THESE LIMITATIONS ALSO APPLY WITH RESPECT TO DAMAGES INCURRED BY
        REASON OF OTHER SERVICES OR PRODUCTS RECEIVED OR ADVERTISED IN
        CONNECTION WITH THE SERVICE. SOME JURISDICTIONS DO NOT ALLOW OR LIMIT
        SOME LIMITATIONS OF LIABILITY, THEREFORE IN SUCH JURISDICTIONS, SOME OF
        THE FOREGOING LIMITATIONS MAY NOT APPLY TO YOU.
      </p>
      <p>
        WE DO NOT WARRANT THAT (I) THE SERVICE WILL MEET YOUR REQUIREMENTS OR
        EXPECTATIONS, (II) THE SERVICE WILL BE UNINTERRUPTED, TIMELY, SECURE, OR
        ERROR-FREE, (III) THE RESULTS THAT MAY BE OBTAINED FROM YOUR USE OF THE
        SERVICE WILL BE ACCURATE OR RELIABLE, (IV) THE QUALITY OF ANY PRODUCTS,
        SERVICES, INFORMATION, CONTENT OR OTHER MATERIAL OBTAINED THROUGH THE
        SERVICE WILL MEET YOUR REQUIREMENTS OR EXPECTATIONS, OR (V) ANY ERRORS
        IN CONTENT WILL BE CORRECTED.
      </p>
      <p>
        ANY CONTENT OBTAINED THROUGH THE USE OF THE SERVICE IS OBTAINED AT YOUR
        OWN DISCRETION AND RISK. YOU ARE SOLELY RESPONSIBLE FOR ANY DAMAGE TO
        YOU, YOUR COMPUTER SYSTEM OR OTHER DEVICE OR LOSS OF DATA THAT RESULTS
        FROM SUCH CONTENT.
      </p>
      <p>
        YOUR SOLE AND EXCLUSIVE RIGHT AND REMEDY IN CASE OF DISSATISFACTION WITH
        THE SERVICE OR ANY OTHER GRIEVANCE SHALL BE THE TERMINATION OF YOUR USE
        OF THE WEBSITE. WITHOUT LIMITING THE FOREGOING, IN NO CASE SHALL THE
        MAXIMUM LIABILITY OF US ARISING FROM OR RELATING TO YOUR USE OF THE
        WEBSITE EXCEED THE AMOUNT PAID BY YOU TO US BY YOU PURSUANT TO THESE
        TERMS IN THE TWELVE (12) MONTHS PRECEDING THE ACCRUAL OF A CLAIM.
      </p>

      <h2>16. Choice of Law and Venue</h2>

      <p>
        To the maximum extent permitted by law, these Terms as well as any
        claim, cause of action, or dispute that may arise between you and us,
        are governed by the laws of the Commonwealth of Massachusetts without
        regard to conflict of law provisions. FOR ANY CLAIM BROUGHT BY EITHER
        PARTY, YOU AGREE TO SUBMIT AND CONSENT TO THE PERSONAL AND EXCLUSIVE
        JURISDICTION IN, AND THE EXCLUSIVE VENUE OF THE COURTS IN MIDDLESEX
        COUNTY, MASSACHUSETTS.
      </p>

      <h2>17. General Terms</h2>

      <ol>
        <li>
          These Terms, as amended from time to time, constitute the entire
          agreement between you and us and supersede all prior agreements
          between you and us and may not be modified without our written
          consent.
        </li>
        <li>
          Our failure to enforce any provision of these Terms will not be
          construed as a waiver of any provision or right.
        </li>
        <li>
          If any part of these Terms is determined to be invalid or
          unenforceable pursuant to applicable law, then the invalid and
          unenforceable provision will be deemed superseded by a valid,
          enforceable provision that most closely matches the intent of the
          original provision and the remainder of the agreement shall continue
          in effect.
        </li>
        <li>
          Nothing herein is intended, nor will be deemed, to confer rights or
          remedies upon any third party.
        </li>
        <li>
          These Terms are not assignable, transferable or sub-licensable by you
          except with our prior written consent, but may be assigned or
          transferred by us without restriction.
        </li>
        <li>
          You agree that we may provide you with notices by e-mail, regular
          mail, or postings to the Service.
        </li>
        <li>
          The section titles in these Terms are for convenience only and have no
          legal or contractual effect.
        </li>
        <li>
          As used in these Terms, the term “including” is illustrative and not
          limitative.
        </li>
        <li>
          If this agreement is translated and executed in any language other
          than English and there is any conflict as between the translation and
          the English version, the English version shall control.
        </li>
      </ol>

      <h2>Contact Us</h2>

      <p>If you have any questions about these Terms, please contact us at:</p>

      <AnimatronAddress contactEmail={contactEmail} />
      <LastUpdated>
        This document was last updated on January 10, 2020
      </LastUpdated>
    </TermsUseStyle>
  </>
);

export default TermsUseSection;
